import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Scrollbars from 'react-custom-scrollbars-2';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ReferralRouteParams } from './types/';

import { IState } from 'models/reducers/i-state.interface';

import IntroSection from 'routes/singleEvent/components/IntroSection/IntroSection';

import RewardDistributionHistorySection from 'routes/singleEvent/components/RewardDistributionHistorySection/RewardDistributionHistorySection';
import AnalyticsEventsSection from './components/AnalyticsEventsSection/AnalyticsEventsSection';
import { AnalyticsParticipationCriteria } from './components/AnalyticsParticipationCriteria';
import { AnalyticsTable } from './components/AnalyticsTable';
import { GamefieldProgress } from './components/GamefieldProgress';
import { ParticipationButton } from './components/ParticipationButton';
import { QuickSummaryStats } from './components/QuickSummaryStats';
import { SectionTitle } from './components/SectionTitle';
import { GlassBox } from 'components/glassBox/GlassBox';
import Page from 'components/page/Page';
import PageWrapper from 'components/pageWrapper/PageWrapper';
import Spacing from 'components/spacing/Spacing';

import { useEventRegistration } from './hooks/useEventRegistration';
import { useEventTypeRedirect } from './hooks/useEventTypeRedirect';
import { useReferralEvent } from './hooks/useReferralEvent';
import { useHomePageNavigate } from 'hooks/useHomePageNavigate';
import { useScroll } from 'hooks/useScroll';

import referralGamefieldBg from 'assets/single-event/referral_gamefield_bg.png';
import referralStatsBg from 'assets/single-event/referral_stats_bg.png';
import referralStatsBgMobile from 'assets/single-event/referral_stats_mobile_bg.png';

import { getParticipationStatus } from './helpers/map-user-status.helper';
import { REFERRAL_CACHE_KEYS } from './constants';

const Badge = ({ text }: { text: string }) => {
  return (
    <div className="tw-flex tw-h-[30px] tw-flex-1 tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-[5px] tw-bg-[#20AA67] tw-px-[10px] tw-font-jost tw-text-[15px] tw-font-semibold tw-text-white lg:tw-flex-none">
      {text}
    </div>
  );
};

const ReferralRoute = () => {
  const AnalyticsChartLazy = lazy(() =>
    import('./components/AnalyticsChart').then((m) => ({ default: m.AnalyticsChart }))
  );
  const { id } = useParams<ReferralRouteParams>();
  const scrollTo = useScroll('scrollbar');
  const { data, analyticsEvents, isLoading, isError, userStatus } = useReferralEvent();
  const walletAddress = useSelector((state: IState): string => state.wallet.address);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const { navigateToHomePage } = useHomePageNavigate();
  const queryClient = useQueryClient();

  const { mutate: registerForEvent, isPending: isRegistering } = useEventRegistration({
    eventId: id!,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [REFERRAL_CACHE_KEYS.REFERRAL_EVENT, id] });
      queryClient.invalidateQueries({ queryKey: [REFERRAL_CACHE_KEYS.USER_METRICS, id] });
      queryClient.invalidateQueries({ queryKey: [REFERRAL_CACHE_KEYS.USER_STATUS, id] });
      setRegistrationError(null);
    },
    onError: (message) => {
      setRegistrationError(message);
    },
  });

  const participationStatus = useMemo(() => {
    return getParticipationStatus(data?.status, registrationError, userStatus, walletAddress);
  }, [data?.status, registrationError, userStatus, walletAddress]);

  const onClickRegister = () => {
    registerForEvent();
  };

  const onClickJoinNow = () => {
    setTimeout(() => {
      scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  useEffect(() => {
    if (!data && !isLoading) {
      navigateToHomePage();
    }
  }, [data, isLoading, navigateToHomePage]);

  useEffect(() => {
    scrollTo({ top: 0, behavior: 'auto' });
  }, [id, scrollTo]);

  useEventTypeRedirect({
    eventId: id,
    eventType: data?.eventType,
    currentRoute: 'referral',
    isLoading,
  });

  return (
    <Page
      className="tw-overflow-x-hidden tw-bg-[#1E1E1E] tw-text-center tw-font-jost"
      pageBackground="dark"
      requireTermsOfServiceConsents
      loading={isLoading}
    >
      <Scrollbars className="scrollbar" style={{ width: '100%', height: '100%' }}>
        <PageWrapper homeFooter={false} landingFooter variant="referral" className="tw-bg-[#101010]">
          {/* Intro section */}
          {data && (
            <IntroSection
              name={data.name}
              tagline={data.tagline}
              iconUrl={data.iconUrl}
              bannerUrl={data.bannerUrl}
              endAt={data.endAt}
              stages={data.stages}
              variant="referral"
              eventInfo={{
                eventId: id!,
                walletAddress,
                userStatus,
              }}
            >
              <div className="tw-mt-[40px] tw-flex tw-flex-col tw-items-center tw-gap-[16px] ">
                <ParticipationButton
                  participationStatus={participationStatus}
                  isRegistering={isRegistering}
                  onClickRegister={onClickRegister}
                  registrationError={registrationError}
                  eligibilityReasons={userStatus?.eligibility?.reasons}
                  hasWallet={!!walletAddress}
                />
              </div>
            </IntroSection>
          )}

          {/* Quick summary stats section */}
          <div className="tw-relative tw-flex tw-flex-col tw-items-center tw-bg-[#101010] tw-py-[50px] lg:tw-px-0">
            {/* Mobile background */}
            <div
              className="tw-absolute tw-inset-0 tw-bg-cover tw-bg-center lg:tw-hidden"
              style={{ backgroundImage: `url(${referralStatsBgMobile})` }}
            />
            {/* Desktop background */}
            <div
              className="tw-absolute tw-inset-0 tw-hidden tw-bg-cover tw-bg-center lg:tw-block"
              style={{ backgroundImage: `url(${referralStatsBg})` }}
            />
            {/* Content */}
            <div className="tw-relative tw-mx-auto tw-w-full tw-max-w-[1280px]">
              {data && (
                <QuickSummaryStats ctaLabel={data.quickSummaryStats.ctaLabel} items={data.quickSummaryStats.items} />
              )}
            </div>
          </div>

          {/* Analytics section */}
          <div className="tw-flex tw-flex-col tw-items-center tw-bg-[#101010] tw-pt-[8px] lg:tw-px-0 lg:tw-py-[50px]">
            <SectionTitle>Analytics</SectionTitle>
            <Spacing height={32} />

            <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1280px] tw-flex-col-reverse tw-items-start tw-justify-center tw-gap-[48px] lg:tw-flex-row">
              <div className="tw-w-full lg:tw-w-[45%]">{data && <AnalyticsTable tableData={data.tableData} />}</div>
              <div className="tw-mt-[-16px] tw-w-full lg:tw-w-[55%]">
                {data && (
                  <Suspense fallback={<div style={{ height: 260 }} />}>
                    <AnalyticsChartLazy chartData={data.chartData} />
                  </Suspense>
                )}
              </div>
            </div>
          </div>

          {/* Gamefield progress section */}
          {data && data.tiers.length > 0 && (
            <div
              className="tw-bg-[#101010] tw-pt-[40px] max-lg:![background-image:none] lg:!tw-bg-cover lg:!tw-bg-center lg:tw-py-[50px]"
              style={{ backgroundImage: `url(${referralGamefieldBg})` }}
            >
              <div className="tw-mx-auto tw-flex tw-max-w-[1280px] tw-flex-col tw-items-center tw-px-[20px] lg:tw-px-0">
                <SectionTitle>Gamified Progress</SectionTitle>
                {data.nextMilestoneName && data.nextRewardAt > data.currentRewards && (
                  <p className="tw-mt-[8px] tw-hidden tw-font-jost tw-text-[18px] tw-text-white lg:tw-block">
                    Every new eligible user pushes you closer to unlocking the next milestone reward!
                  </p>
                )}
                <Spacing height={40} mobileHeight={2} />
                <GlassBox
                  gradient="linear-gradient(to right, #2D5EFF 0%, #389FFF 100%)"
                  borderWidth="1px"
                  className="tw-flex tw-w-full tw-flex-col tw-gap-[40px] tw-px-[20px] tw-py-[20px] lg:tw-px-[40px]"
                  contentClassName="tw-hidden lg:tw-block"
                >
                  <GamefieldProgress
                    currentRewards={data.currentRewards}
                    nextRewardAt={data.nextRewardAt}
                    tiers={data.tiers}
                    circularTitle={data.circularProgressOverlay.title}
                    nextRewardName={data.circularProgressOverlay.nextRewardName}
                  />
                  <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-[10px] lg:tw-flex-nowrap">
                    {data.badges.map((badge, index) => (
                      <Badge key={index} text={badge.text} />
                    ))}
                  </div>
                </GlassBox>
              </div>
            </div>
          )}

          {/* Vip referral event section */}
          {analyticsEvents.length > 0 && (
            <div className="tw-flex tw-flex-col tw-items-center tw-bg-[#101010] tw-px-[20px] tw-py-[40px] lg:tw-px-0 lg:tw-pt-[50px]">
              <SectionTitle>VIP Referral Event</SectionTitle>
              <Spacing height={40} />
              <AnalyticsEventsSection analyticsEvents={analyticsEvents} />
            </div>
          )}

          {/* Reward distribution history section */}
          <RewardDistributionHistorySection eventId={id} />

          {/* Participation criteria section */}
          {data && data.participationCriteria.length > 0 && (
            <AnalyticsParticipationCriteria
              participationCriteria={data.participationCriteria}
              onClickJoinNow={onClickJoinNow}
            />
          )}
        </PageWrapper>
      </Scrollbars>
    </Page>
  );
};

export default ReferralRoute;
